import PropTypes from 'prop-types'
import React from 'react'

import { LocalizationContext } from 'contexts/Localization'

import {
  SuggestionsWrapper,
  SuggestionsItem,
  SearchText,
  NotFoundItem,
} from './styles'

const Suggestions = ({
  suggestions,
  onSelect,
  searchWord,
  active,
  wrapperRef,
}) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [isArrowControl, setIsArrowControl] = React.useState(false)
  const suggestionWrapper = React.useRef()

  const onSelectHandler = React.useCallback(() => {
    if (suggestions) {
      onSelect(suggestions[selectedIndex])
    }
  }, [onSelect, selectedIndex, suggestions])

  const scrollToItem = React.useCallback(index => {
    if (suggestionWrapper.current) {
      const scrollElement = suggestionWrapper.current.getScrollElement()
      const contentElement = suggestionWrapper.current.getContentElement()
      setIsArrowControl(true)

      const scrollIndex = index - 1
      scrollElement.scrollTop =
        contentElement.children[scrollIndex < 0 ? 0 : scrollIndex]?.offsetTop
    }
  }, [])

  const keyDownHandler = React.useCallback(
    e => {
      const { key } = e
      if (key === 'Enter') {
        e.preventDefault()
        onSelectHandler()
      }

      if (key === 'ArrowDown') {
        e.preventDefault()
        const plusValue = selectedIndex + 1
        const resultValue = plusValue > suggestions.length - 1 ? 0 : plusValue
        setSelectedIndex(resultValue)
        scrollToItem(resultValue)
      }
      if (key === 'ArrowUp') {
        e.preventDefault()
        const minusValue = selectedIndex - 1
        const resultValue = minusValue < 0 ? suggestions.length - 1 : minusValue
        setSelectedIndex(resultValue)
        scrollToItem(resultValue)
      }
    },
    [onSelectHandler, scrollToItem, selectedIndex, suggestions],
  )

  React.useEffect(() => {
    if (wrapperRef && wrapperRef.current) {
      const wrapper = wrapperRef.current

      wrapper.addEventListener('keydown', keyDownHandler)
      return () => {
        wrapper.removeEventListener('keydown', keyDownHandler)
      }
    }
  }, [keyDownHandler, wrapperRef])

  React.useEffect(() => {
    setSelectedIndex(-1)
  }, [scrollToItem, searchWord])

  const { dict } = React.useContext(LocalizationContext)
  const suggestionItems = React.useMemo(() => {
    return suggestions?.length ? (
      suggestions?.map((item, index) => {
        return (
          <SuggestionsItem
            selected={selectedIndex === index}
            onMouseOver={() => !isArrowControl && setSelectedIndex(index)}
            onMouseDown={onSelectHandler}
            key={item.value}
          >
            <SearchText
              searchWords={searchWord?.split(' ') || []}
              autoEscape={true}
              textToHighlight={item.value}
            />
          </SuggestionsItem>
        )
      })
    ) : (
      <NotFoundItem
        dangerouslySetInnerHTML={{ __html: dict.errors.address_not_found }}
      />
    )
  }, [
    suggestions,
    dict.errors.address_not_found,
    selectedIndex,
    onSelectHandler,
    searchWord,
    isArrowControl,
  ])

  return active ? (
    <SuggestionsWrapper
      onMouseMove={() => setIsArrowControl(false)}
      ref={suggestionWrapper}
    >
      {suggestionItems}
    </SuggestionsWrapper>
  ) : null
}

Suggestions.propTypes = {
  onSelect: PropTypes.func.isRequired,
  searchWord: PropTypes.string,
  suggestions: PropTypes.array,
  wrapperRef: PropTypes.object,
  active: PropTypes.bool,
}

Suggestions.defaultProps = {
  searchWord: '',
  suggestions: null,
}

export default Suggestions
